import { YoungQuestion, YoungAnswer, YoungSchemasResult } from '@/types/personality';

// Função para calcular os resultados do teste de Esquemas de Young
export const calculateYoungSchemasScores = (questions: YoungQuestion[], answers: YoungAnswer[]): YoungSchemasResult => {
  // Inicializar contadores para cada esquema
  const schemaCounts: Record<string, { sum: number, count: number }> = {
    emotional_deprivation: { sum: 0, count: 0 },
    abandonment: { sum: 0, count: 0 },
    mistrust_abuse: { sum: 0, count: 0 },
    social_isolation: { sum: 0, count: 0 },
    defectiveness: { sum: 0, count: 0 },
    failure: { sum: 0, count: 0 },
    dependence: { sum: 0, count: 0 },
    vulnerability: { sum: 0, count: 0 },
    enmeshment: { sum: 0, count: 0 },
    entitlement: { sum: 0, count: 0 },
    insufficient_self_control: { sum: 0, count: 0 },
    subjugation: { sum: 0, count: 0 },
    self_sacrifice: { sum: 0, count: 0 },
    approval_seeking: { sum: 0, count: 0 },
    negativity: { sum: 0, count: 0 },
    emotional_inhibition: { sum: 0, count: 0 },
    unrelenting_standards: { sum: 0, count: 0 },
    punitiveness: { sum: 0, count: 0 },
  };

  // Mapear cada questão para seu esquema correspondente
  const questionSchemaMap = questions.reduce<Record<number, string>>((map, question) => {
    map[question.id] = question.schema;
    return map;
  }, {});

  // Calcular a soma e contagem para cada esquema
  answers.forEach(answer => {
    const schema = questionSchemaMap[answer.questionId];
    if (schema && schemaCounts[schema]) {
      schemaCounts[schema].sum += answer.score;
      schemaCounts[schema].count += 1;
    }
  });

  // Calcular a média para cada esquema
  const schemaScores: Record<string, number> = {};
  for (const [schema, { sum, count }] of Object.entries(schemaCounts)) {
    schemaScores[schema] = count > 0 ? sum / count : 0;
  }

  // Calcular os percentis com base nas médias dos esquemas
  const calculatePercentile = (score: number): number => {
    // Simples cálculo de percentil baseado na escala de 1-6
    return ((score - 1) / 5) * 100;
  };

  const schemaPercentiles: Record<string, number> = {};
  for (const [schema, score] of Object.entries(schemaScores)) {
    schemaPercentiles[schema] = calculatePercentile(score);
  }

  // Definir os domínios e seus esquemas associados - CORRIGIDO
  const domains = {
    disconnection: ['emotional_deprivation', 'abandonment', 'mistrust_abuse', 'social_isolation', 'defectiveness'],
    impaired_autonomy: ['failure', 'dependence', 'vulnerability', 'enmeshment'],
    impaired_limits: ['entitlement', 'insufficient_self_control'],
    other_directedness: ['subjugation', 'self_sacrifice', 'approval_seeking'],
    overvigilance: ['negativity', 'emotional_inhibition', 'unrelenting_standards', 'punitiveness'],
  };

  // Calcular médias por domínio
  const domainScores: Record<string, number> = {};
  for (const [domain, schemas] of Object.entries(domains)) {
    let sum = 0;
    let count = 0;
    schemas.forEach(schema => {
      if (schemaScores[schema]) {
        sum += schemaScores[schema];
        count += 1;
      }
    });
    domainScores[domain] = count > 0 ? sum / count : 0;
  }

  // Encontrar os esquemas mais significativos (acima de um certo valor)
  const significantThreshold = 4.0;
  const significantSchemas = Object.entries(schemaScores)
    .filter(([, score]) => score >= significantThreshold)
    .map(([schema]) => schema);

  // Encontrar os domínios mais significativos
  const significantDomains = Object.entries(domainScores)
    .filter(([, score]) => score >= significantThreshold)
    .map(([domain]) => domain);

  // Construir o objeto de resultados
  return {
    timestamp: new Date().toISOString(),
    scores: schemaScores,
    percentiles: schemaPercentiles,
    domainScores,
    significantSchemas,
    significantDomains,
    // Valores individuais para cada esquema para armazenamento na tabela
    emotional_deprivation: schemaScores.emotional_deprivation || 0,
    abandonment: schemaScores.abandonment || 0,
    mistrust_abuse: schemaScores.mistrust_abuse || 0,
    social_isolation: schemaScores.social_isolation || 0,
    defectiveness: schemaScores.defectiveness || 0,
    failure: schemaScores.failure || 0,
    dependence: schemaScores.dependence || 0,
    vulnerability: schemaScores.vulnerability || 0,
    enmeshment: schemaScores.enmeshment || 0,
    entitlement: schemaScores.entitlement || 0,
    insufficient_self_control: schemaScores.insufficient_self_control || 0,
    subjugation: schemaScores.subjugation || 0,
    self_sacrifice: schemaScores.self_sacrifice || 0,
    approval_seeking: schemaScores.approval_seeking || 0,
    negativity: schemaScores.negativity || 0,
    emotional_inhibition: schemaScores.emotional_inhibition || 0,
    unrelenting_standards: schemaScores.unrelenting_standards || 0,
    punitiveness: schemaScores.punitiveness || 0,
  };
};

// Função para obter descrições dos esquemas
export const getSchemaDescriptions = (schema: string): { title: string, description: string } => {
  const descriptions: Record<string, { title: string, description: string }> = {
    emotional_deprivation: {
      title: "Privação Emocional",
      description: "Expectativa de que necessidades emocionais normais - como carinho, empatia e proteção - não serão atendidas por outros."
    },
    abandonment: {
      title: "Abandono",
      description: "Crença de que as pessoas próximas eventualmente deixarão você e você ficará emocionalmente isolado."
    },
    mistrust_abuse: {
      title: "Desconfiança/Abuso",
      description: "Expectativa de que os outros irão ferir, abusar, humilhar ou tirar vantagem de você."
    },
    social_isolation: {
      title: "Isolamento Social",
      description: "Sentimento de que você está isolado do mundo, diferente das outras pessoas, e não pertence a nenhum grupo."
    },
    defectiveness: {
      title: "Defectividade/Vergonha",
      description: "Sentimento de que se é defeituoso, mau ou não amável; e que se os outros descobrirem, não poderão amar você."
    },
    failure: {
      title: "Fracasso",
      description: "Crença de que você falhou, vai falhar, ou é fundamentalmente inadequado em relação aos seus pares."
    },
    dependence: {
      title: "Dependência/Incompetência",
      description: "Crença de que você é incapaz de lidar com responsabilidades cotidianas sem ajuda significativa dos outros."
    },
    vulnerability: {
      title: "Vulnerabilidade",
      description: "Medo exagerado de que catástrofes iminentes ocorrerão a qualquer momento e que você não poderá evitá-las."
    },
    enmeshment: {
      title: "Emaranhamento",
      description: "Envolvimento emocional e proximidade excessivos com pessoas significativas, à custa da individualização normal."
    },
    entitlement: {
      title: "Merecimento/Grandiosidade",
      description: "Crença de que você é superior aos outros e tem direitos e privilégios especiais."
    },
    insufficient_self_control: {
      title: "Autocontrole Insuficiente",
      description: "Dificuldade persistente em exercer autocontrole e tolerar frustração para atingir metas pessoais."
    },
    subjugation: {
      title: "Subjugação",
      description: "Rendição excessiva de controle aos outros para evitar raiva, retaliação ou abandono."
    },
    self_sacrifice: {
      title: "Autossacrifício",
      description: "Foco excessivo em atender voluntariamente as necessidades dos outros em situações cotidianas, à custa da própria gratificação."
    },
    approval_seeking: {
      title: "Busca de Aprovação",
      description: "Ênfase excessiva em ganhar aprovação, reconhecimento ou atenção dos outros."
    },
    negativity: {
      title: "Negativismo/Pessimismo",
      description: "Foco generalizado nos aspectos negativos da vida, minimizando os positivos, com expectativa de que tudo acabará mal."
    },
    emotional_inhibition: {
      title: "Inibição Emocional",
      description: "Inibição excessiva de ação espontânea, sentimentos ou comunicação para evitar desaprovação, sentimentos de vergonha ou perda de controle."
    },
    unrelenting_standards: {
      title: "Padrões Inflexíveis",
      description: "Crença de que é necessário atingir padrões internalizados muito altos de comportamento e desempenho."
    },
    punitiveness: {
      title: "Punição",
      description: "Crença de que as pessoas devem ser severamente punidas por erros, incluindo você mesmo."
    }
  };

  return descriptions[schema] || { 
    title: "Esquema Não Identificado", 
    description: "Descrição não disponível para este esquema."
  };
};

// Função para obter recomendações baseadas nos esquemas de Young
export const getYoungSchemasRecommendations = (results: YoungSchemasResult): Array<{
  title: string;
  description: string;
  type: 'article' | 'exercise' | 'therapy';
  schema: string;
}> => {
  const recommendations: Array<{
    title: string;
    description: string;
    type: 'article' | 'exercise' | 'therapy';
    schema: string;
  }> = [];

  // Obter os 3 esquemas mais significativos
  const topSchemas = Object.entries(results.scores)
    .sort(([, scoreA], [, scoreB]) => scoreB - scoreA)
    .slice(0, 3)
    .map(([schema]) => schema);

  // Recomendações específicas para cada esquema
  const schemaRecommendations: Record<string, Array<{
    title: string;
    description: string;
    type: 'article' | 'exercise' | 'therapy';
  }>> = {
    emotional_deprivation: [
      {
        title: "Diário de Necessidades Emocionais",
        description: "Mantenha um diário para identificar suas necessidades emocionais não atendidas e encontrar formas saudáveis de satisfazê-las.",
        type: "exercise"
      },
      {
        title: "Terapia Focada em Esquemas para Privação Emocional",
        description: "A terapia focada em esquemas pode ajudar a processar experiências passadas de privação e desenvolver formas de atender suas próprias necessidades.",
        type: "therapy"
      }
    ],
    abandonment: [
      {
        title: "Técnicas de Ancoragem",
        description: "Aprenda exercícios de mindfulness para se acalmar quando os medos de abandono surgem.",
        type: "exercise"
      },
      {
        title: "Construindo Segurança nos Relacionamentos",
        description: "Artigo sobre como desenvolver relacionamentos mais seguros e estáveis.",
        type: "article"
      }
    ],
    // Adicionar mais recomendações para outros esquemas...
    defectiveness: [
      {
        title: "Desafios de Autoafirmação",
        description: "Exercícios diários para identificar e desafiar crenças negativas sobre si mesmo.",
        type: "exercise"
      },
      {
        title: "Grupo de Apoio para Autoestima",
        description: "Participar de grupos onde possa compartilhar e receber feedback positivo de outros.",
        type: "therapy"
      }
    ],
    unrelenting_standards: [
      {
        title: "Definindo Expectativas Realistas",
        description: "Aprenda a estabelecer metas alcançáveis e a celebrar pequenas vitórias.",
        type: "exercise"
      },
      {
        title: "Mindfulness para Perfeccionismo",
        description: "Técnicas de atenção plena para reduzir pressão interna e apreciar o processo, não apenas os resultados.",
        type: "article"
      }
    ]
  };

  // Adicionar recomendações para os esquemas mais significativos
  topSchemas.forEach(schema => {
    const recommendations_for_schema = schemaRecommendations[schema];
    if (recommendations_for_schema) {
      recommendations_for_schema.forEach(rec => {
        recommendations.push({
          ...rec,
          schema: schema
        });
      });
    }
  });

  // Adicionar recomendações gerais
  recommendations.push({
    title: "Entendendo seus Esquemas",
    description: "Um guia completo sobre como identificar e trabalhar com seus esquemas emocionais para melhorar seu bem-estar.",
    type: "article",
    schema: "general"
  });

  return recommendations;
};
